/* Odette & Opal product card interactions: color image swap plus size chip quick add.

   The variant map used to be inlined next to every card. On the home page that
   was 74 copies and a quarter of the compressed document, paid by every
   visitor whether or not they ever touched a swatch. Liquid already renders
   the chips for the default colour, so the map is only needed the moment
   somebody picks a different one. It is now fetched once per card from the
   product's own .js endpoint, warmed on hover, and the chips are held disabled
   in the gap so a click cannot add the colour that was showing before. */
(function () {
  class BrandCard extends HTMLElement {
    constructor() {
      super();
      this.data = null;
      this._dataPromise = null;
      this._token = 0;
      /* The size panel renders inside .card__media so it can overlay the
         picture, which puts it outside this element. Bind across the whole
         card instead of just our own subtree. */
      this.scope = this.closest('.card-wrapper') || this.closest('.card') || this;
      this.image = this.scope.querySelector('.card__media img');
      this.colorName = this.querySelector('[data-color-name]');
      this.activeColor = this.dataset.defaultColor || null;
      this.bind();
    }

    bind() {
      this.querySelectorAll('.swatch[data-color]').forEach((sw) => {
        sw.addEventListener('mouseenter', () => this.selectColor(sw.dataset.color, sw));
        sw.addEventListener('click', (e) => { e.preventDefault(); this.selectColor(sw.dataset.color, sw); });
      });
      this.scope.querySelectorAll('.size-chip[data-variant-id]').forEach((chip) => {
        chip.addEventListener('click', (e) => {
          e.preventDefault();
          if (chip.hasAttribute('disabled')) return;
          this.add(chip.dataset.variantId, chip);
        });
      });
      /* Warm the map as soon as the card is approached, so by the time a
         swatch is reached the answer is usually already here. */
      const warm = () => this.ensureData();
      this.scope.addEventListener('mouseenter', warm, { once: true });
      this.scope.addEventListener('focusin', warm, { once: true });
      this.scope.addEventListener('touchstart', warm, { once: true, passive: true });
    }

    ensureData() {
      if (this._dataPromise) return this._dataPromise;
      const url = this.dataset.productUrl;
      if (!url) {
        this._dataPromise = Promise.resolve(null);
        return this._dataPromise;
      }
      this._dataPromise = fetch(url + '.js', { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then((p) => {
          if (!p || !p.variants) return null;
          const names = (p.options || []).map((o) => String(o && o.name ? o.name : o).toLowerCase());
          const ci = names.findIndex((n) => n.indexOf('color') > -1 || n.indexOf('colour') > -1);
          const si = names.findIndex((n) => n.indexOf('size') > -1);
          this.data = {
            variants: p.variants.map((v) => ({
              id: v.id,
              c: ci > -1 ? v.options[ci] : '',
              s: si > -1 ? v.options[si] : '',
              a: !!v.available,
            })),
          };
          return this.data;
        })
        .catch(() => null);
      return this._dataPromise;
    }

    chips() {
      return this.scope.querySelectorAll('.size-chip[data-size]');
    }

    setPending(on) {
      this.chips().forEach((chip) => {
        chip.classList.toggle('is-pending', on);
        if (on) {
          chip.setAttribute('disabled', '');
        } else if (!chip.classList.contains('is-soldout')) {
          chip.removeAttribute('disabled');
        }
      });
    }

    applySizes(color) {
      if (!this.data || !this.data.variants) return;
      this.chips().forEach((chip) => {
        const v = this.data.variants.find((x) => x.c === color && x.s === chip.dataset.size);
        if (!v) {
          chip.classList.add('is-soldout');
          chip.setAttribute('disabled', '');
          return;
        }
        chip.dataset.variantId = v.id;
        chip.classList.toggle('is-soldout', !v.a);
        if (v.a) chip.removeAttribute('disabled');
        else chip.setAttribute('disabled', '');
      });
    }

    selectColor(color, sw) {
      this.activeColor = color;
      this.querySelectorAll('.swatch[data-color]').forEach((s) => s.setAttribute('aria-pressed', s === sw));
      if (this.colorName) this.colorName.textContent = color;
      const img = sw && sw.dataset.image;
      if (img && this.image) {
        this.image.src = img;
        this.image.srcset = '';
      }

      /* Nothing to correct: the chips on screen were rendered for this colour. */
      if (!this.data && color === this.dataset.defaultColor) return;

      if (this.data) {
        this.applySizes(color);
        return;
      }

      const token = ++this._token;
      this.setPending(true);
      this.ensureData().then(() => {
        if (token !== this._token) return;
        this.setPending(false);
        this.applySizes(this.activeColor);
      });
    }

    async add(variantId, chip) {
      chip.classList.add('is-loading');
      try {
        const res = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
        });
        if (!res.ok) throw new Error('add failed');
        await res.json();
        this.openCart();
      } catch (e) {
        window.location.href = this.dataset.productUrl || '/';
      } finally {
        chip.classList.remove('is-loading');
      }
    }

    openCart() {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.renderContents === 'function') {
        fetch(`${window.Shopify.routes.root}?sections=cart-drawer,cart-icon-bubble`)
          .then((r) => r.json())
          .then((sections) => {
            drawer.renderContents({ sections });
            drawer.open?.();
          })
          .catch(() => { window.location.href = window.Shopify.routes.root + 'cart'; });
      } else {
        window.location.href = window.Shopify.routes.root + 'cart';
      }
    }
  }
  if (!customElements.get('brand-card')) customElements.define('brand-card', BrandCard);
})();
